import React from "react";
import { Link } from "react-router-dom";
import ClientShell from "@/components/ClientShell";
import { useAuth } from "@/context/AuthContext";
import {
  User,
  Truck,
  Receipt,
  Certificate,
  ShieldCheck,
  Buildings,
  Question,
  SignOut,
  CaretRight,
} from "@phosphor-icons/react";

const ITEMS = [
  { to: "/profile", label: "Profile", desc: "Your account & password", icon: User, testid: "more-profile" },
  { to: "/documents/delivery-orders", label: "DO", desc: "Delivery orders", icon: Truck, testid: "more-do" },
  { to: "/documents/invoices", label: "Invoices", desc: "All order invoices", icon: Receipt, testid: "more-invoices" },
  { to: "/documents/certificates", label: "Certificates", desc: "Quality & test certificates", icon: Certificate, testid: "more-certificates" },
  { to: "/documents/warranty", label: "Warranty", desc: "Product warranty details", icon: ShieldCheck, testid: "more-warranty" },
  { to: "/projects", label: "Projects", desc: "Order-specific project info", icon: Buildings, testid: "more-projects" },
  { to: "/help", label: "Help & Support", desc: "Email, call or visit our showroom", icon: Question, testid: "more-help" },
];

export default function More() {
  const { user, logout } = useAuth();

  return (
    <ClientShell>
      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Account</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-2">
          More
        </h1>
      </div>

      {/* User card */}
      <div className="bg-zinc-900 text-white p-5 md:p-6 mb-6 flex items-center gap-4">
        <div className="w-14 h-14 bg-white text-zinc-900 grid place-items-center font-black text-xl uppercase">
          {user?.name?.[0] || "?"}
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-lg truncate">{user?.name}</div>
          <div className="text-sm text-white/60 truncate">{user?.email}</div>
          {user?.company && <div className="mt-tiny text-white/50 mt-1">{user.company}</div>}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 divide-y divide-zinc-100" data-testid="more-menu">
        {ITEMS.map(({ to, label, desc, icon: Icon, testid }) => (
          <Link
            key={to}
            to={to}
            data-testid={testid}
            className="flex items-center gap-4 p-4 md:p-5 hover:bg-zinc-50 transition-colors group"
          >
            <div className="w-11 h-11 bg-zinc-100 group-hover:bg-[#0047AB]/10 grid place-items-center transition-colors">
              <Icon size={20} weight="duotone" className="text-[#0047AB]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-zinc-900">{label}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
            </div>
            <CaretRight size={16} className="text-zinc-400 group-hover:translate-x-1 transition-transform" />
          </Link>
        ))}
        <button
          onClick={logout}
          data-testid="more-logout"
          className="w-full text-left flex items-center gap-4 p-4 md:p-5 hover:bg-red-50 transition-colors group"
        >
          <div className="w-11 h-11 bg-red-50 group-hover:bg-red-100 grid place-items-center transition-colors">
            <SignOut size={20} weight="duotone" className="text-red-600" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-red-600">Log out</div>
            <div className="text-xs text-red-500/80 mt-0.5">End your session</div>
          </div>
          <CaretRight size={16} className="text-red-400 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </ClientShell>
  );
}
